// tutorContextBus — a lightweight, app-wide store the Tutor FAB reads to
// build its system prompt. Anything that knows useful context (the unified
// chat shell, a playbook page, the mastery roadmap) can call
// `setTutorContext({...})` and the next tutor question will include it.
//
// Intentionally not a React context: most callers are deep components that
// fire on intermittent events (playbook loaded, step changed). A plain
// module-level store + emitter avoids prop-drilling and re-renders.

export type TutorContext = {
  page?: string;
  currentPlaybook?: string;
  currentStep?: string;
  currentModel?: string;
  masteryProgress?: { launched?: number; total?: number };
  /** Last few user/assistant turns from the active chat shell, so the tutor
   *  can answer with continuity ("you just asked about X — here's the next
   *  step"). Kept short — TutorSheet caps the upstream payload anyway. */
  recentMessages?: { role: "user" | "assistant"; content: string }[];
};

let snapshot: TutorContext = {};
const listeners = new Set<(ctx: TutorContext) => void>();

export const setTutorContext = (patch: Partial<TutorContext>) => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((fn) => fn(snapshot));
};

export const clearTutorContext = (keys?: (keyof TutorContext)[]) => {
  if (!keys) {
    snapshot = {};
  } else {
    const next = { ...snapshot };
    keys.forEach((k) => delete next[k]);
    snapshot = next;
  }
  listeners.forEach((fn) => fn(snapshot));
};

export const getTutorContext = (): TutorContext => snapshot;

export const subscribeTutorContext = (fn: (ctx: TutorContext) => void): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};